import { useLocation } from 'react-router-dom';
import { ArrowRight, Bot, ExternalLink, Info, Plug, Scale, ShieldCheck, Workflow } from 'lucide-react';
import { PageStructuredData } from '@/components/seo/PageStructuredData';
import { StructuredData } from '@/components/seo/StructuredData';
import { CheckList, CtaPanel, DataTable, FaqSection, HeroActions, InShort, PageHero, PageShell, RelatedLinks, Reveal, Section, SectionHeading, SmartLink, StepFlow } from '@/components/page/PageKit';
import { Button } from '@/components/ui/button';
import { getMcpGuide, mcpGuideUrl, type AtsMcpGuide } from '@/data/atsMcpGuides';
import { MCP_SERVER_URL } from '@/data/mcpContent';
import { articleSchema, faqSchema } from '@/seo/schemas';
import { useSEO } from '@/hooks/useSEO';
import NotFound from '@/pages/NotFound';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const formatDate = (iso: string) => {
  const [year, month, day] = iso.split('-').map(Number);
  return `${day} ${MONTHS[month - 1]} ${year}`;
};

function GuidePage({ g }: { g: AtsMcpGuide }) {
  const url = mcpGuideUrl(g.slug);
  const headline = `${g.h1[0]} ${g.h1[1]}`;
  const integration = g.atsSlug ? { href: `/integrations/${g.atsSlug}`, label: `Beatview with ${g.vendor}`, body: `How Beatview works alongside ${g.vendor} as your system of record.` } : { href: '/integrations', label: 'Beatview integrations', body: 'How Beatview connects to your ATS through Zapier, a native integration or MCP.' };
  useSEO({ title: g.seoTitle, description: g.description, canonicalUrl: url, ogType: 'article' });

  return (
    <PageShell>
      <PageStructuredData name={headline} description={g.description} url={url} dateModified={g.reviewed} breadcrumbs={[{ name: 'Resources', url: 'https://beatview.ai/resources' }, { name: 'Recruiting MCP servers', url: 'https://beatview.ai/guides/recruiting-mcp-servers' }, { name: `${g.vendor} MCP` }]} />
      <StructuredData data={[articleSchema({ headline, description: g.description, url, datePublished: g.published, dateModified: g.reviewed, about: { '@type': 'SoftwareApplication', name: g.vendor, url: g.website, applicationCategory: 'BusinessApplication' } }), faqSchema(g.faqs)]} />
      <article>
        <PageHero breadcrumbs={[{ name: 'Resources', href: '/resources' }, { name: 'MCP servers', href: '/guides/recruiting-mcp-servers' }, { name: `${g.vendor} MCP` }]} eyebrow={`MCP guide · Checked ${formatDate(g.reviewed)}`} title={<>{g.h1[0]} <span className="bv-hero-highlight">{g.h1[1]}</span></>}>
          <InShort label="The short answer">{g.shortAnswer}</InShort>
          <div className="mt-7"><HeroActions primaryLabel="Add Beatview’s MCP server free" primaryHref="/mcp" secondary={{ label: 'Compare recruiting MCP servers', href: '/guides/recruiting-mcp-servers' }} note={`Every ${g.vendor} detail comes from ${g.vendor}’s own documentation, read on ${formatDate(g.reviewed)}. Beatview is not affiliated with ${g.vendor}.`} /></div>
        </PageHero>

        <Section tone="white" id="at-a-glance" labelledBy="glance-title">
          <SectionHeading id="glance-title" eyebrow="At a glance" title={g.hasOfficial ? `${g.vendor} MCP at a glance` : `${g.vendor} and AI assistants at a glance`} />
          <Reveal delay={0.1} className="mt-10">
            <DataTable caption={`${g.vendor} MCP facts, checked ${formatDate(g.reviewed)}`} columns={['', 'What the documentation says']} rows={g.facts.map(f => [<span key={f.label} className="font-semibold text-bv-ink">{f.label}</span>, f.value])} minWidth={620} />
          </Reveal>
        </Section>

        {g.adminSteps && g.adminSteps.length > 0 && (
          <Section tone="base" labelledBy="admin-title">
            <SectionHeading id="admin-title" eyebrow={g.adminEyebrow ?? 'Step 1'} title={g.adminTitle ?? `What an admin sets up in ${g.vendor}`} description={g.adminIntro} />
            <div className="mt-12"><StepFlow steps={g.adminSteps} /></div>
          </Section>
        )}

        {g.clientSetups.length > 0 && (
          <Section tone="white" labelledBy="connect-title">
            <SectionHeading id="connect-title" eyebrow={g.adminSteps?.length ? 'Step 2' : 'Setup'} title={g.connectTitle ?? `Connect ${g.vendor} to your AI assistant`} description={g.connectIntro} />
            <div className="mt-10 grid gap-5 lg:grid-cols-2">
              {g.clientSetups.map((c, i) => (
                <Reveal key={c.client} delay={(i % 2) * 0.05}>
                  <div className="flex h-full min-w-0 flex-col rounded-3xl border border-bv-blue-line bg-white p-6 sm:p-7">
                    <h3 className="font-display text-xl font-semibold text-bv-ink">{c.client}</h3>
                    <ol className="mt-4 list-decimal space-y-2 pl-5 text-[15px] leading-7 text-bv-ink2 [overflow-wrap:anywhere]">{c.steps.map(s => <li key={s}>{s}</li>)}</ol>
                    {c.note && <p className="mt-4 rounded-2xl bg-bv-base p-4 text-[13.5px] leading-6 text-bv-ink2">{c.note}</p>}
                  </div>
                </Reveal>
              ))}
            </div>
          </Section>
        )}

        {g.capabilities.length > 0 && (
          <Section tone="wash" labelledBy="caps-title">
            <SectionHeading id="caps-title" eyebrow="What it can do" title={g.capabilitiesTitle ?? `What ${g.vendor}’s MCP server can read and change`} description={g.capabilitiesIntro} />
            <Reveal delay={0.1} className="mt-10">
              <DataTable caption={`What ${g.vendor} documents for AI assistants`} columns={['Area', 'Read', 'Change']} rows={g.capabilities.map(c => [<span key={c.area} className="font-semibold text-bv-ink">{c.area}</span>, c.read, c.write])} minWidth={760} />
            </Reveal>
            {g.cannot && g.cannot.length > 0 && (
              <Reveal delay={0.15}>
                <div className="mt-8 rounded-3xl border border-bv-blue-line bg-white p-6 sm:p-7">
                  <h3 className="font-display text-lg font-semibold text-bv-ink">Not documented, so plan without it</h3>
                  <CheckList className="mt-4" icon="minus" items={g.cannot} />
                </div>
              </Reveal>
            )}
          </Section>
        )}

        <Section tone="white" labelledBy="limits-title">
          <SectionHeading id="limits-title" eyebrow="Limits and security" title={g.limitsTitle ?? `${g.vendor} MCP limits, permissions and audit`} />
          <div className="mt-10 grid gap-5 lg:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-3xl border border-bv-blue-line bg-white p-6 sm:p-7">
                <h3 className="font-display text-lg font-semibold text-bv-ink">Limits to know</h3>
                <CheckList className="mt-4" icon="minus" items={g.limits} />
              </div>
            </Reveal>
            <Reveal delay={0.06}>
              <div className="h-full rounded-3xl border border-bv-blue-line bg-white p-6 sm:p-7">
                <h3 className="font-display text-lg font-semibold text-bv-ink">{g.securityTitle ?? 'Permissions and audit'}</h3>
                <CheckList className="mt-4" items={g.security} />
              </div>
            </Reveal>
          </div>
        </Section>

        {g.prompts.length > 0 && (
          <Section tone="base" labelledBy="prompts-title">
            <SectionHeading id="prompts-title" eyebrow="Use cases" title={g.promptsTitle ?? `What teams ask ${g.vendor} through MCP`} description={g.promptsIntro} />
            <ul className="mt-10 grid gap-4 md:grid-cols-2">
              {g.prompts.map(p => <li key={p} className="rounded-2xl border border-bv-blue-line bg-white p-5 text-[15px] leading-7 text-bv-ink">“{p}”</li>)}
            </ul>
          </Section>
        )}

        <Section tone="dark" labelledBy="beatview-title">
          <SectionHeading tone="dark" id="beatview-title" eyebrow="Add screened candidates" title={g.beatviewTitle ? <>{g.beatviewTitle[0]} <span className="bv-hero-highlight">{g.beatviewTitle[1]}</span></> : <>{g.vendor} gives your assistant the records. <span className="bv-hero-highlight">Beatview adds the screening.</span></>} description={g.beatview} />
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {[
              { icon: Plug, title: 'One address', body: `Add ${MCP_SERVER_URL} to Claude, ChatGPT, Gemini or Cursor${g.hasOfficial ? `, next to ${g.vendor}` : ''}. It is included on every Beatview plan, including Free.` },
              { icon: Bot, title: 'Evidence, not just records', body: 'Beatview returns each applicant’s resume match score with reasons and a structured AI interview assessed answer by answer.' },
              { icon: ShieldCheck, title: 'You stay in charge', body: 'The assistant confirms with you before moving a candidate or sending an interview invitation, and recordings never leave Beatview.' },
            ].map((item, i) => (
              <Reveal key={item.title} delay={i * 0.05}>
                <div className="h-full rounded-3xl border border-white/10 bg-white/5 p-6">
                  <item.icon className="h-5 w-5 text-bv-orange" />
                  <h3 className="mt-4 font-display text-lg font-semibold text-white">{item.title}</h3>
                  <p className="mt-2 text-[15px] leading-7 text-white/70">{item.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-8 rounded-3xl border border-white/10 bg-white/5 p-6">
            <p className="text-[13px] font-semibold uppercase tracking-wider text-white/50">{g.combinedLabel ?? 'Try with both connected'}</p>
            <ul className="mt-3 space-y-2 text-[15px] leading-7 text-white/85">{g.combinedPrompts.map(p => <li key={p}>“{p}”</li>)}</ul>
          </div>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button asChild size="lg" className="bv-shine h-[54px] rounded-xl px-7 text-base font-semibold"><SmartLink href="/mcp">How Beatview’s MCP server works <ArrowRight className="h-4 w-4" /></SmartLink></Button>
            <Button asChild size="lg" variant="outline" className="h-[54px] rounded-xl border-white/20 bg-white/5 px-7 text-base font-semibold text-white hover:bg-white/10 hover:text-white"><SmartLink href={integration.href}>{integration.label}</SmartLink></Button>
          </div>
        </Section>

        <FaqSection title={`${g.vendor} MCP: frequently asked questions`} items={g.faqs} idPrefix={`${g.slug}-faq`} tone="base" />

        <Section tone="white" narrow id="sources" labelledBy="sources-title">
          <SectionHeading id="sources-title" eyebrow="Sources" title="Where these details come from" description={`${g.vendor}’s own documentation${g.clientSources ? ' and each AI assistant’s help centre' : ''}, read on ${formatDate(g.reviewed)}. MCP servers change quickly; check the source before you rely on a detail.`} />
          <Reveal delay={0.1}>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {[...g.sources, ...(g.clientSources ?? [])].map(source => (
                <li key={source.url}><a href={source.url} target="_blank" rel="noopener noreferrer nofollow" className="group flex h-full items-center justify-between gap-3 rounded-2xl border border-bv-blue-line bg-white px-5 py-4 text-[15px] font-semibold text-bv-ink transition-colors hover:border-bv-blue/40 hover:text-bv-blue">{source.label}<ExternalLink className="h-4 w-4 shrink-0" /></a></li>
              ))}
            </ul>
            <div className="mt-5 flex gap-3 rounded-2xl border border-bv-blue-line bg-bv-base p-4 text-[13.5px] leading-6 text-bv-ink2">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-bv-blue" />
              <p>Beatview publishes this guide and runs its own MCP server, which works alongside {g.vendor}. Where {g.vendor} does not document something, we say so rather than guess. Spotted a change? <SmartLink href="/contact" className="font-semibold text-bv-blue hover:underline">Tell us</SmartLink>.</p>
            </div>
          </Reveal>
        </Section>
      </article>

      <RelatedLinks title="More on recruiting and MCP" tone="base" items={[
        { title: 'Recruiting MCP servers compared', body: 'Every ATS and recruiting tool with an official MCP server, side by side.', href: '/guides/recruiting-mcp-servers', icon: Scale },
        { title: 'Beatview MCP server', body: 'Screened and interviewed candidates in Claude, ChatGPT and Gemini, on every plan.', href: '/mcp', icon: Bot },
        { title: integration.label, body: integration.body, href: integration.href, icon: Workflow },
      ]} />
      <CtaPanel eyebrow="Start free" title={<>Give your assistant <span className="bv-hero-highlight">candidates already screened.</span></>} description="Beatview’s MCP server is included on every plan, including Free. Connect it next to your ATS in two minutes." secondary={{ label: 'See the MCP server', href: '/mcp' }} />
    </PageShell>
  );
}

export function AtsMcpGuidePage({ slug }: { slug: string }) {
  const g = getMcpGuide(slug);
  return g ? <GuidePage g={g} /> : <NotFound />;
}

/** Route component: the slug is the last path segment, e.g. /guides/greenhouse-mcp. */
export default function AtsMcpGuideRoute() {
  const { pathname } = useLocation();
  return <AtsMcpGuidePage slug={pathname.replace(/\/$/, '').split('/').pop() ?? ''} />;
}
